import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Banknote } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: user?.name || "", phone: user?.phone || "", address_line: "", city: "Doha", area: "", notes: "" });
  const [method, setMethod] = useState("cod");
  const [loading, setLoading] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-24 px-6 text-center" data-testid="must-login">
        <h2 className="font-display font-bold text-2xl">{t("checkout.mustLogin")}</h2>
        <button onClick={() => navigate("/login")} className="btn-maroon px-6 py-3 mt-6 font-bold">
          {t("nav.login")}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        items: items.map((it) => ({ product_id: it.product.id, quantity: it.qty })),
        shipping: form,
        payment_method: method,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/orders", payload);
      if (method === "stripe" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      clear();
      toast.success(lang === "ar" ? "تم تأكيد الطلب" : "Order confirmed");
      navigate("/orders");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="checkout-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-8">{t("checkout.title")}</h1>
      <form onSubmit={onSubmit} className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-8">
          <section className="border border-slate-200 p-6">
            <div className="label-mono text-slate-500 mb-4">{t("checkout.shipping")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label={t("checkout.fullName")} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="ship-name" />
              <Input label={t("checkout.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="ship-phone" />
              <Input label={t("checkout.addressLine")} value={form.address_line} onChange={(v) => setForm({ ...form, address_line: v })} required testid="ship-address" full />
              <Input label={t("checkout.city")} value={form.city} onChange={(v) => setForm({ ...form, city: v })} required testid="ship-city" />
              <Input label={t("checkout.area")} value={form.area} onChange={(v) => setForm({ ...form, area: v })} testid="ship-area" />
              <Input label={t("checkout.notes")} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="ship-notes" full />
            </div>
          </section>

          <section className="border border-slate-200 p-6">
            <div className="label-mono text-slate-500 mb-4">{t("checkout.paymentMethod")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <PayOption icon={<Banknote className="w-5 h-5" />} active={method === "cod"} onClick={() => setMethod("cod")} title={t("checkout.cod")} testid="pay-cod" />
              <PayOption icon={<CreditCard className="w-5 h-5" />} active={method === "stripe"} onClick={() => setMethod("stripe")} title={t("checkout.stripe")} testid="pay-stripe" />
            </div>
          </section>
        </div>

        <aside className="border border-slate-200 p-6 h-fit sticky top-24">
          <div className="label-mono text-slate-500 mb-4">{t("checkout.orderTotal")}</div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {items.map((it) => (
              <div key={it.product.id} className="flex justify-between text-sm">
                <span className="truncate me-3">{(lang === "ar" ? it.product.name_ar : it.product.name_en)} × {it.qty}</span>
                <span className="font-bold whitespace-nowrap">{(it.product.price * it.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between items-end">
            <span className="font-display font-bold">{t("cart.total")}</span>
            <span className="font-display font-black text-2xl text-[#8D1B3D]">
              {total.toFixed(2)} {t("common.currency")}
            </span>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-maroon w-full mt-6 py-4 font-bold tracking-wide disabled:opacity-60"
            data-testid="place-order-btn"
          >
            {loading ? t("checkout.processing") : t("checkout.placeOrder")}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, required, full, testid }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <div className="label-mono text-slate-500 mb-1.5">{label}{required ? " *" : ""}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm"
        data-testid={testid}
      />
    </label>
  );
}

function PayOption({ icon, active, onClick, title, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 p-4 border text-start ${active ? "border-[#8D1B3D] bg-[#8D1B3D]/5" : "border-slate-200 hover:border-slate-400"}`}
      data-testid={testid}
    >
      <div className={`w-10 h-10 flex items-center justify-center ${active ? "bg-[#8D1B3D] text-white" : "bg-slate-100"}`}>{icon}</div>
      <div className="font-bold text-sm">{title}</div>
    </button>
  );
}
